import {
  DailyCheckInBonus,
  DailyCheckInCampaign,
  DailyCheckInCampaignType,
  DailyCheckInDay,
  DailyCheckInRewardStatus
} from '@apeiron/reward'
import { ApeironBlueButton, MayNull } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import CheckInBonusCard from '@src/components/reward/checkIn/CheckInBonusCard'
import CheckInDayCard from '@src/components/reward/checkIn/CheckInDayCard'
import CheckInMakeupPopup from '@src/components/reward/checkIn/CheckInMakeupPopup'
import RoninFreeTransactionMessage from '@src/components/wallet/RoninFreeTransactionMessage'
import { useDailyCheckInContext } from '@src/contexts/reward/DailyCheckInContext'
import { addDays, startOfDay } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useMemo } from 'react'
import ReactCountdown, { CountdownRenderProps, zeroPad } from 'react-countdown'
import { Trans, useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  border: 2px solid #c6af8f;
  background-color: #31343e;
  padding: 30px;
  gap: 20px;
`

const TopContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`

const CardContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
`

const CountdownContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`

const CountdownTextContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-right: auto;
`

const CheckInBonusCardContainer = styled(Box)`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;

  gap: 20px;
`

const MainCountdownText = styled(Typography)`
  color: #838383;
  font-size: 20px;
  font-weight: 300;
  strong {
    color: white;
    font-weight: 400;
  }
`

const CountdownDescriptionText = styled(Typography)`
  color: #838383;
  font-size: 12px;
  font-weight: 300;
  white-space: pre-line;
`

const HeaderTitleText = styled(Typography)`
  color: white;
  font-size: 18px;
  font-weight: 400;
  line-height: 40px;
  strong {
    color: #ffaa00;
  }
`

const HeaderDesciptionText = styled(Typography)`
  color: white;
  font-size: 12px;
  font-weight: 300;
`

const HeaderMissedText = styled(Typography)`
  color: white;
  font-size: 16px;
  font-weight: 300;
  margin-left: auto;
  strong {
    font-weight: 400;
    color: #ff6937;
  }
`

const MakeupButton = styled(ApeironBlueButton)`
  padding: 10px 14px;
  height: 90%;
  border-radius: 5px;
`

const StyledRoninFreeTransactionMessage = styled(RoninFreeTransactionMessage)`
  border-top: 1px solid #00000022;
  padding-top: 10px;
`

const CheckInCountdown: FC<CheckInCountdownProps> = (
  props: CheckInCountdownProps
) => {
  const { campaign, onComplete } = props

  const { t } = useTranslation()

  const handleOnNowUTC = useCallback(() => {
    const now = new Date()

    const utcNow = utcToZonedTime(now, 'Etc/UTC')

    return utcNow.getTime()
  }, [])

  const tomorrow = useMemo(() => {
    const utcNow = handleOnNowUTC()

    return startOfDay(addDays(utcNow, 1))
  }, [handleOnNowUTC])

  const claimed = useMemo((): boolean => {
    if (R.isNotNil(campaign)) {
      const checkClaimedToday = R.pipe<any, DailyCheckInDay[], boolean>(
        R.filter((day: DailyCheckInDay) => {
          const operated =
            day.status === DailyCheckInRewardStatus.Claimable ||
            day.status === DailyCheckInRewardStatus.Claimed

          return day.isCurrent && operated
        }),
        R.complement(R.isEmpty)
      )

      return checkClaimedToday(campaign.days)
    }

    return false
  }, [campaign])

  const handleOnRender = useCallback(
    (props: CountdownRenderProps) => {
      const { hours, minutes, seconds } = props

      return claimed ? (
        <MainCountdownText>
          <Trans>
            {t('adeptus.reward.checkin.countdown', {
              timer: `${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`
            })}
          </Trans>
        </MainCountdownText>
      ) : (
        <></>
      )
    },
    [claimed, t]
  )

  return (
    <ReactCountdown
      date={tomorrow}
      renderer={handleOnRender}
      now={handleOnNowUTC}
      onComplete={onComplete}
    />
  )
}

const CheckInDays: FC<Props> = (props: Props) => {
  const { className } = props

  const { t } = useTranslation()

  const {
    myDailyCheckInCampaign,
    fetchMyDailyCheckInCampaign,
    setShowMakeupPopup,
    getMissedDaysCount
  } = useDailyCheckInContext()

  const campaign = useMemo((): MayNull<DailyCheckInCampaign> => {
    return myDailyCheckInCampaign?.[0]
  }, [myDailyCheckInCampaign])

  const daysChecked = useMemo((): number => {
    if (R.isNotNil(campaign)) {
      const countChecked = R.pipe<any, DailyCheckInDay[], number>(
        R.filter((day: DailyCheckInDay) => {
          return (
            day.status === DailyCheckInRewardStatus.Claimable ||
            day.status === DailyCheckInRewardStatus.Claimed
          )
        }),
        R.length
      )

      return countChecked(campaign.days)
    }

    return 0
  }, [campaign])

  const daysMissed = useMemo((): number => {
    return getMissedDaysCount(campaign)
  }, [campaign, getMissedDaysCount])

  // TODO - the sorting should be done on backend
  const daysSorted = useMemo(() => {
    if (
      RA.isNotNil(myDailyCheckInCampaign) &&
      myDailyCheckInCampaign.length > 0
    ) {
      return myDailyCheckInCampaign[0]?.days.sort(
        (a: DailyCheckInDay, b: DailyCheckInDay) =>
          a.cycle * 7 + a.day - (b.cycle * 7 + b.day)
      )
    } else return []
  }, [myDailyCheckInCampaign])

  // TODO - the sorting should be done on backend
  const bonusSorted = useMemo(() => {
    if (
      RA.isNotNil(myDailyCheckInCampaign) &&
      myDailyCheckInCampaign.length > 0
    ) {
      return myDailyCheckInCampaign[0]?.bonuses.sort(
        (a: DailyCheckInBonus, b: DailyCheckInBonus) =>
          a.meta.index - b.meta.index
      )
    } else return []
  }, [myDailyCheckInCampaign])

  const handleOnCountdownComplete = useCallback(() => {
    fetchMyDailyCheckInCampaign({
      campaignTypes: [DailyCheckInCampaignType.WeeklyStardust]
    })
  }, [fetchMyDailyCheckInCampaign])

  const handleOnMakeup = useCallback(() => {
    setShowMakeupPopup(true)
  }, [setShowMakeupPopup])

  return (
    <>
      <TopContainer>
        <CountdownTextContainer>
          <HeaderTitleText>
            <Trans>
              {t('adeptus.reward.checkin.count', { count: daysChecked })}
            </Trans>
          </HeaderTitleText>
          <HeaderDesciptionText>
            {t('adeptus.reward.checkin.count_description')}
          </HeaderDesciptionText>
        </CountdownTextContainer>
        <HeaderMissedText>
          <Trans>
            {t('adeptus.reward.checkin.missed_count', { count: daysMissed })}
          </Trans>
        </HeaderMissedText>
        <MakeupButton disabled={daysMissed === 0} onClick={handleOnMakeup}>
          {t('adeptus.reward.checkin.makeup')}
        </MakeupButton>
      </TopContainer>
      <MainContainer className={className}>
        <CardContainer>
          {RA.mapIndexed((day: DailyCheckInDay, index: number) => {
            return (
              <CheckInDayCard
                key={index}
                dayInfo={day}
                campaignId={campaign?.campaignID}
              />
            )
          }, daysSorted)}
        </CardContainer>
        <CountdownContainer>
          <CountdownTextContainer>
            <CheckInCountdown
              campaign={campaign}
              onComplete={handleOnCountdownComplete}
            />
            <CountdownDescriptionText>
              {t('adeptus.reward.checkin.countdown_description')}
            </CountdownDescriptionText>
          </CountdownTextContainer>
          <CheckInBonusCardContainer>
            {RA.mapIndexed((bonus: DailyCheckInBonus, index: number) => {
              return (
                <CheckInBonusCard
                  key={index}
                  bonusInfo={bonus}
                  dayInfo={daysSorted}
                  campaignId={campaign?.campaignID}
                />
              )
            }, bonusSorted)}
          </CheckInBonusCardContainer>
        </CountdownContainer>
        <StyledRoninFreeTransactionMessage />
        <CheckInMakeupPopup />
      </MainContainer>
    </>
  )
}

type CheckInCountdownProps = {
  campaign: MayNull<DailyCheckInCampaign>
  onComplete: () => void
}

type Props = {
  className?: string
}

export default CheckInDays
